import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Breadcrumb } from './breadcrumb';

class BreadcrumbDefinition {

  constructor(
    public pageName: string,
    public children: string[],
    public url: string
  ) {}

  public isParentOf(name: string): boolean {
    return this.children.includes(name);
  }
}

@Injectable({ providedIn: 'root' })
export class BreadcrumbsService {
  private navigationHistory: BreadcrumbDefinition[] = [];
  private readonly STORAGE_KEY = 'breadcrumbs-history';
  private readonly RETURN_URL_KEY = 'breadcrumbs-return-url';
  private readonly RETURN_URL_PAGE_KEY = 'breadcrumbs-return-url-page';
  private returnUrlOverride: string | null = null;
  /**
   * Page sur laquelle le retour forcé s'applique : la première page atteinte après l'avoir posé.
   * Sur une page plus profonde (ex. une inscription ouverte depuis la session), le fil d'Ariane reprend la main.
   */
  private returnUrlOverridePage: string | null = null;
  public readonly breadcrumbUpdated = new EventEmitter<void>();

  public setReturnUrlOverride(url: string | null): void {
    this.returnUrlOverride = url;
    this.returnUrlOverridePage = null;
    this.persistReturnUrlOverride();
  }

  constructor(private router: Router) {
    this.loadHistory();
    this.loadReturnUrlOverride();
  }

  private shouldIgnoreUrl(url: string, breadcrumb: Breadcrumb): boolean {
    return breadcrumb.ignorePath.some(p => url.endsWith(p));
  }

  public notifyNavigation(path: Breadcrumb, url: string): void {
    const cp = this.currentPage;
    if (!cp || cp.pageName !== path.name) {
      if (!this.shouldIgnoreUrl(url, path)) {
        if (this.returnUrlOverride != null && this.returnUrlOverridePage == null) {
          this.returnUrlOverridePage = path.name;
          this.persistReturnUrlOverride();
        }
        this.navigationHistory.push(new BreadcrumbDefinition(path.name, path.children, url));
        this.breadcrumbUpdated.emit();
        this.saveHistory();
      }
    } else if (cp.pageName === path.name) {
      if (!this.shouldIgnoreUrl(url, path)) {
        cp.url = url;
        this.breadcrumbUpdated.emit();
        this.saveHistory();
      }
    }
  }

  public get currentPage(): BreadcrumbDefinition | null {
    return this.navigationHistory.at(-1) ?? null;
  }

  /** Le retour forcé ne vaut que sur sa page (ou partout tant qu'aucune nouvelle page n'a été atteinte). */
  private get isReturnUrlOverrideApplicable(): boolean {
    return this.returnUrlOverride != null
      && (this.returnUrlOverridePage == null || this.returnUrlOverridePage === this.currentPage?.pageName);
  }

  public hasRetour(): boolean {
    if (this.isReturnUrlOverrideApplicable) return true;
    const current = this.currentPage;
    if (!current) return false;
    const parent = this.getClosestParentOf(current.pageName);
    return !!parent?.url;
  }

  private getClosestParentOf(pageName: string): BreadcrumbDefinition | undefined {
    return [...this.navigationHistory]
      .reverse()
      .find(entry => entry.isParentOf(pageName));
  }

  private clearHistoryUntil(target: BreadcrumbDefinition): void {
    while (this.navigationHistory.length > 0 && this.navigationHistory.at(-1) !== target) {
      this.navigationHistory.pop();
      this.saveHistory();
    }
  }

  public clearHistory(): void {
    this.navigationHistory = [];
    this.saveHistory();
    this.clearReturnUrlOverride();
  }

  public navigateToPageRetour(): void {
    if (this.isReturnUrlOverrideApplicable) {
      const url = this.returnUrlOverride;
      this.clearReturnUrlOverride();
      this.router.navigateByUrl(url);
      return;
    }
    if (!this.hasRetour()) return;

    const parent = this.getClosestParentOf(this.currentPage.pageName);
    if (parent) {
      this.clearHistoryUntil(parent);
      this.router.navigateByUrl(parent.url);
    }
  }

  private saveHistory(): void {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.navigationHistory));
    } catch (e) {
      console.warn('Échec de la sauvegarde de l’historique dans le storage', e);
    }
  }

  private loadHistory(): void {
    try {
      const data = sessionStorage.getItem(this.STORAGE_KEY);
      if (data) {
        const parsed = JSON.parse(data);
        this.navigationHistory = parsed.map((item: any) =>
          new BreadcrumbDefinition(item.pageName, item.children, item.url)
        );
      }
    } catch (e) {
      console.warn('Échec du chargement de l’historique depuis le storage', e);
      this.navigationHistory = [];
    }
  }

  public getAndClearReturnUrlOverride(): string | null {
    if (!this.isReturnUrlOverrideApplicable) {
      return null;
    }
    const url = this.returnUrlOverride;
    this.clearReturnUrlOverride();
    return url;
  }

  private clearReturnUrlOverride(): void {
    this.returnUrlOverride = null;
    this.returnUrlOverridePage = null;
    try {
      sessionStorage.removeItem(this.RETURN_URL_KEY);
      sessionStorage.removeItem(this.RETURN_URL_PAGE_KEY);
    } catch {
      // ignore
    }
  }

  private persistReturnUrlOverride(): void {
    try {
      if (this.returnUrlOverride) {
        sessionStorage.setItem(this.RETURN_URL_KEY, this.returnUrlOverride);
      } else {
        sessionStorage.removeItem(this.RETURN_URL_KEY);
      }
      if (this.returnUrlOverridePage) {
        sessionStorage.setItem(this.RETURN_URL_PAGE_KEY, this.returnUrlOverridePage);
      } else {
        sessionStorage.removeItem(this.RETURN_URL_PAGE_KEY);
      }
    } catch {
      // ignore
    }
  }

  private loadReturnUrlOverride(): void {
    try {
      this.returnUrlOverride = sessionStorage.getItem(this.RETURN_URL_KEY);
      this.returnUrlOverridePage = sessionStorage.getItem(this.RETURN_URL_PAGE_KEY);
    } catch {
      this.returnUrlOverride = null;
      this.returnUrlOverridePage = null;
    }
  }
}
